// import { hot } from 'react-hot-loader'; // must be first loaded
import React from 'react';
import { Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react'
import styled from 'styled-components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import InviteAccept from 'common/InviteAccept';
import ResetPassword from 'common/ResetPassword';
import ResetPasswordEmailEntry from 'common/ResetPasswordEmailEntry';
import ErrorBoundary from 'common/ErrorBoundary';
import LoginPage from 'components/Auth/Login';
import { SocketProvider } from 'context/socket';
import { AuthProvider, useAuth } from 'lib/Auth';
import { Color } from 'lib/styles';
import GlobalStyle from 'styles/global';
import { STORAGE_KEYS } from 'constants/general';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import store, { persistor } from 'redux/store';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { themeMui } from './styles/themeMui';
import { CampaignContextProvider } from 'context/campaign';
import AppContainer from 'common/AppContainer';

const Container = styled.div`
  color: ${Color.black};
  overflow-x: hidden;
`;

// Wrapper components to access route params
const InviteAcceptWrapper = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // If token is present, use token flow; otherwise use legacy id flow
  if (token) {
    return <InviteAccept token={token} />;
  }
  return <InviteAccept inviteId={id} />;
};

const ResetPasswordWrapper = () => {
  const { id } = useParams();
  return <ResetPassword resetId={id} />;
};

function Main() {
  const { user } = useAuth();
  const userStorageData = window.sessionStorage.getItem(STORAGE_KEYS.AUTH);

  // Default page based on auth status
  const defaultPage = user && userStorageData !== null ? <AppContainer /> : <LoginPage />;

  return (
    <Container>
      <Routes>
        <Route path="/accept-invite" element={<InviteAcceptWrapper />} />
        <Route path="/accept-invite/:id" element={<InviteAcceptWrapper />} />
        <Route path="/reset-password" element={<ResetPasswordEmailEntry />} />
        <Route path="/reset-password/:id" element={<ResetPasswordWrapper />} />
        <Route path="/*" element={defaultPage} />
      </Routes>
    </Container>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <AuthProvider>
          <SocketProvider>
            <CampaignContextProvider>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={themeMui}>
                  <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <GlobalStyle />
                    <ErrorBoundary>
                      <Main />
                    </ErrorBoundary>
                    <ToastContainer
                      autoClose={5000}
                      limit={1}
                      position="top-right"
                      hideProgressBar
                      icon={false}
                      transition={Slide}
                      style={{ left: 'calc(100vw - 346px)' }}
                    />
                  </LocalizationProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </CampaignContextProvider>
          </SocketProvider>
        </AuthProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
